import { Fragment, useRef, useEffect, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import dayjs from 'dayjs'
import { styled } from '@mui/material'
import { useTranslation } from 'src/utils/i18n'

import { motion } from 'framer-motion'
import { DatePicker } from 'antd'
import { useAndroidDevicePagedData } from 'src/hooks/useAndroidDevicePagedData'
import { buildTimeParams, buildPresetTimeParams } from 'src/utils/dateRangeParams'
import { useDispatch } from 'react-redux'
import { setCurrentTab } from 'src/store/apps/Common'
import ContentLoading from 'src/components/ContentLoading'

// 自定义隐藏滚动条且保留滚动特性
const ScrollDiv = styled('div')({
  '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
  scrollbarWidth: 'thin',  /* 适用于 Firefox，设置滚动条为细 */
  '&::-webkit-scrollbar': {
    width: '4px',  /* 设置滚动条的宽度为 4px */
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#888',  /* 设置滚动条滑块的颜色 */
    borderRadius: '10px',  /* 设置滑块圆角 */
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',  /* 设置滚动条轨道的颜色 */
  },
  '@media (max-width: 600px)': {
    '-msOverflowStyle': 'none',  /* 适用于 IE 和 Edge */
    scrollbarWidth: 'none',  /* 适用于 Firefox */
    '&::-webkit-scrollbar': {
      display: 'none', /* 适用于 WebKit 浏览器（如 Chrome、Safari 和新版 Edge） */
    }
  }
})

const Keylogger = () => {
  const { t } = useTranslation('common')
  const dispatch = useDispatch()
  const [isExpand, setIsExpand] = useState(false)//展开日期选择
  const [showData, setShowData] = useState([])//展示数据
  const [showDate, setShowDate] = useState(false)//展示日期
  const [date, setDate] = useState(['All'])//日期
  const [from, setFrom] = useState('')//开始日期
  const [to, setTo] = useState('')//结束日期
  const [fromDate, setFromDate] = useState(null)//开始日期(dayjs)
  const [toDate, setToDate] = useState(null)//结束日期(dayjs)
  const [fromTime, setFromTime] = useState('')//开始日期时间戳
  const [toTime, setToTime] = useState('')//结束日期时间戳
  const [searchData, setSearchData] = useState('')//搜索内容
  const [dates, setDates] = useState([])
  const { context, data: allData, loading: isLoading, loadNext, setDateRange } = useAndroidDevicePagedData({
    source: 'local',
    category: 'KEY_LOGGER',
    limit: 100
  })

  useEffect(() => {
    setShowData([])
    setIsExpand(false)
    setShowDate(false)
    setDate(['All'])
    setFrom('')
    setTo('')
    setFromDate(null)
    setToDate(null)
    setFromTime('')
    setToTime('')
    setSearchData('')
    setDates([])
  }, [context?.requestKey])

  const handleImageError = event => {
    event.target.src = '/images/android/com.example.variousdata.png'
  }
  const dom = useRef(null)

  // 滚动百分比
  const [scroll, setScroll] = useState(0)

  // 获取滚动百分比
  useEffect(() => {
    const handScroll = () => {
      if (dom.current) {
        const { scrollTop, scrollHeight, clientHeight } = dom.current
        const progress = (scrollTop / (scrollHeight - clientHeight)) * 100
        setScroll(progress.toFixed(0))
      }
    }
    const currentRef = dom.current
    if (currentRef) {
      currentRef.addEventListener('scroll', handScroll)
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handScroll)
      }
    }
  }, [])

  // 监听滚动百分比
  useEffect(() => {
    if (scroll >= 100) loadNext()
  }, [loadNext, scroll])

  const onApply = () => {
    if (!from || !to) return
    setDate([from, to])
    setShowDate(false)
  }

  //筛选数据
  useEffect(() => {
    const filteredByName = allData.filter(item =>
      item.app.toLowerCase().includes(searchData.toLowerCase())
    )

    let results = filteredByName

    // 如果有 fromTime 和 toTime，则进一步筛选
    if (fromTime && toTime) {
      results = results.filter(item => {
        const itemTime = dayjs(item.time).valueOf()

        return fromTime <= itemTime && itemTime <= toTime
      })
    }

    // 更新显示数据
    setShowData(results)
  }, [fromTime, toTime, allData, searchData])

  //更新 fromTime 和 toTime（同时把区间交给接口筛选）
  useEffect(() => {
    if (date.length === 1 && date[0] !== 'All') {
      const { startTime, endTime } = buildPresetTimeParams(date[0])
      setFromTime(startTime)
      setToTime(endTime)
      setDateRange({ startTime, endTime })
    } else if (date.length === 2) {
      const { startTime, endTime } = buildTimeParams(date)
      setFromTime(startTime)
      setToTime(endTime)
      setDateRange({ startTime, endTime })
    } else {
      setFromTime('')
      setToTime('')
      setDateRange(null)
    }
  }, [date, setDateRange])

  // 日期按钮 + 下拉框（dropdown 用 portal 渲染到 body，避免被 sticky header 的白底遮挡）
  const buttonRef = useRef(null)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 })

  const updateDropdownPos = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + 6,
        right: Math.max(8, window.innerWidth - rect.right)
      })
    }
  }, [])

  useEffect(() => {
    if (showDate) {
      updateDropdownPos()
      window.addEventListener('resize', updateDropdownPos)
      window.addEventListener('scroll', updateDropdownPos, true)
      return () => {
        window.removeEventListener('resize', updateDropdownPos)
        window.removeEventListener('scroll', updateDropdownPos, true)
      }
    }
  }, [showDate, updateDropdownPos])

  const button = () => (
    <div
      ref={buttonRef}
      className='min-w-[172px] w-auto h-[40px] bg-[#2A99FF] border border-[#E5E7EB] rounded-[8px] flex justify-center items-center cursor-pointer relative px-[10px] whitespace-nowrap'
      onClick={e => {
        e.stopPropagation()
        if (!showDate) updateDropdownPos()
        setShowDate(val => !val)
      }}
    >
      <Image className='ml-[2px]' src='/images/record-screen/calendar2.svg' width={20} height={20} alt='calendar' />
      <span className='text-[14px] leading-[16px] text-white mx-[10px] whitespace-nowrap'>
        {date.length === 1 && date[0]}
        {date.length === 2 && (
          <>
            {date[0]} ~ {date[1]}
          </>
        )}
      </span>
      <motion.div initial={{ rotate: 0 }} animate={{ rotate: showDate ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <Image src='/images/record-screen/union.svg' width={16} height={16} alt='drop down' />
      </motion.div>
    </div>
  )

  const dropdown =
    showDate && typeof document !== 'undefined'
      ? createPortal(
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
            className='fixed z-[800] w-[340px] bg-[#FFFFFF] rounded-[12px] shadow-[0_8px_28px_rgba(0,0,0,0.14)] border border-[#EFF0F2] overflow-hidden'
            style={{ top: dropdownPos.top, right: dropdownPos.right }}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            {/* 预设快捷筛选 */}
            <div className='px-[16px] pt-[14px] pb-[12px] border-b border-[#F2F3F5]'>
              <div className='text-[13px] font-[600] text-[#1F2329] mb-[10px]'>{t('Quick filter')}</div>
              <div className='flex flex-wrap gap-[8px]'>
                {['All', 'Today', 'Last 7 days', 'Last 14 days', 'Last 30 days', 'Last Year'].map(preset => {
                  const active = date.length === 1 && date[0] === preset
                  return (
                    <div
                      key={preset}
                      className={`px-[12px] py-[5px] text-[12px] rounded-[8px] cursor-pointer transition-colors ${
                        active
                          ? 'bg-[#2A99FF] text-white'
                          : 'bg-[#F4F6F9] text-[#525866] hover:bg-[#E8F1FF] hover:text-[#2A99FF]'
                      }`}
                      onClick={() => {
                        setDate([preset])
                        setShowDate(false)
                      }}
                    >
                      {t(preset)}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* 自定义日期范围 */}
            <div className='px-[16px] py-[14px]'>
              <div className='text-[13px] font-[600] text-[#1F2329] mb-[10px]'>{t('Custom range')}</div>
              <div className='flex items-center gap-[8px]'>
                <DatePicker
                  className='flex-1'
                  picker='date'
                  format='YYYY-MM-DD'
                  placeholder={t('Start date')}
                  value={fromDate}
                  onChange={(date, dateString) => {
                    setFromDate(date)
                    setFrom(dateString)
                  }}
                  allowClear
                />
                <span className='text-[#BBB] text-[14px]'>-</span>
                <DatePicker
                  className='flex-1'
                  picker='date'
                  format='YYYY-MM-DD'
                  placeholder={t('End date')}
                  value={toDate}
                  onChange={(date, dateString) => {
                    setToDate(date)
                    setTo(dateString)
                  }}
                  allowClear
                />
              </div>
            </div>

            {/* 底部操作 */}
            <div className='flex border-t border-[#F2F3F5]'>
              <div
                className='flex-1 py-[11px] text-center text-[14px] text-[#525866] cursor-pointer hover:bg-[#F7F8FA]'
                onClick={() => setShowDate(false)}
              >
                {t('Cancel')}
              </div>
              <div className='w-[1px] bg-[#F2F3F5]' />
              <div
                className={`flex-1 py-[11px] text-center text-[14px] ${
                  from && to ? 'text-[#2A99FF] font-[600] cursor-pointer hover:bg-[#F0F7FF]' : 'text-[#A8ABB3] cursor-not-allowed'
                }`}
                onClick={() => onApply()}
              >
                {t('apply')}
              </div>
            </div>
          </motion.div>,
          document.body
        )
      : null

  useEffect(() => {
    const title = t('Keylogger')
    dispatch(setCurrentTab(title))

    return () => dispatch(setCurrentTab(''))
  }, [dispatch, t])

  // 埋点上报事件
  // useEffect(() => {
  //   if (allData.length <= 0) return
  //   if (Array.isArray(allData) && allData.length > 0) {
  //     const events = allData.map(item => ({
  //       event_name: 'Info_Open_Web_Keylogger_Info',
  //       properties: {
  //         app: item?.app || '',
  //         text: item?.text || '',
  //         time: item?.time || '',
  //       }
  //     }))
  //   }
  // }, [allData])

  return (
    <Fragment>
      <ScrollDiv className='w-full !h-[calc(var(--vh2)*100-215px)] smj:!h-[calc(var(--vh2)*100-92px)] [box-shadow:0px_0px_12px_0px_#00000021] rounded-[10px] px-[48px] sm:px-[10px] bg-white relative'>
        <div className='w-full'>
          <div className='sticky top-0 bg-[#FFFFFF]'>
            <div className='flex justify-between items-center pt-[30px] pb-[12px]'>
              <div className='flex items-center w-[75%] smj:w-[50%]'>
                <div className='w-[53%] relative smj:w-[100%]'>
                  <div className='h-[40px] w-[165px] sm:w-[145px] border-[#E3E3E5] border-[1px] rounded-[8px] flex items-center justify-center cursor-pointer'
                    onClick={() => { setIsExpand(!isExpand) }}>
                    <div className='text-[16px] text-[#000000] font-[600] mr-[24px] sm:mr-[12px]'>{searchData || t('App name')}</div>
                    <Image src='/images/record-screen/drop-down.svg' width={20} height={20} alt='expand and collapse' className={`${isExpand && 'rotate-[180deg]'} transition-all duration-300 sm:w-[16px] sm:h-[16px]`} />
                  </div>
                  {isExpand &&
                    <div className='absolute z-10 bg-[#FFFFFF] rounded-[6px] mt-[2px] px-2 py-4 h-auto w-[165px] [box-shadow:0px_0px_3px_0px_#0000001A]'>
                      <div className="relative flex justify-end">
                        <input className="w-full text-[14px] leading-[21px] placeholder:text-[#666666] text-[#282938] border-[#666CFF] border-[1px] rounded-[4px] font-[400] pl-2 py-1"
                          placeholder={t('Search app')}
                          onInput={(e) => { setSearchData(e.target.value) }} />
                        <div className='absolute top-0 right-0 flex items-center h-full pr-2'>
                          <Image src="/images/contacts/search.svg" className='w-[16px] h-[16px]' width={16} height={16} alt='Search' />
                        </div>
                      </div>
                      <div className='px-2 py-1 text-[#222222] text-[16px] leading-[24px] rounded-[4px] hover:bg-[#EAEAEA] w-full mt-[14px] cursor-pointer'
                        onClick={() => { setSearchData('Chrome'); setIsExpand(!isExpand) }}>Chrome</div>
                      <div className='px-2 py-1 text-[#222222] text-[16px] leading-[24px] rounded-[4px] hover:bg-[#EAEAEA] w-full my-[10px] cursor-pointer'
                        onClick={() => { setSearchData('WhatsApp'); setIsExpand(!isExpand) }}>WhatsApp</div>
                      <div className='px-2 py-1 text-[#222222] text-[16px] leading-[24px] rounded-[4px] hover:bg-[#EAEAEA] w-full cursor-pointer'
                        onClick={() => { setSearchData('Instagram'); setIsExpand(!isExpand) }}>Instagram</div>
                      <div className='px-2 py-1 text-[#222222] text-[16px] leading-[24px] rounded-[4px] hover:bg-[#EAEAEA] w-full mt-[10px] cursor-pointer'
                        onClick={() => { setSearchData('Snapchat'); setIsExpand(!isExpand) }}>Snapchat</div>
                    </div>}
                </div>
                <div className='text-[16px] leading-[24px] text-[#222222] font-[600] smj:hidden'>{t('Text')}</div>
              </div>
              <div>{button()}</div>
            </div>
          </div>
          {dropdown}
          <div className='overflow-auto !h-[calc(var(--vh2)*100-298px)] smj:!h-[calc(var(--vh2)*100-176px)]' ref={dom}>
            {/* pc端 */}
            {showData.map((item, index) => (
              <div key={index} className={`text-[16px] text-[#2222222] leading-[24px] flex justify-between items-center px-3 py-[18px] smj:hidden border-b border-b-[#DCDCDC] `}>
                <div className='flex items-center w-[75%]'>
                  <div className={`flex items-center w-[53%]`}>
                    <Image src={'https://cos.spyx.com/' + item.iconUrl} width={42} height={42} alt='icon' className='w-[42px] h-[42px] mr-[21px]' onError={handleImageError} />
                    <span className='font-[500]'>{item.app}</span>
                  </div>
                  <div className='font-[400] ml-[21px] flex-1 min-w-0 break-words'>{item.text}</div>
                </div>
                <div>{dayjs(item.time).format('DD/MM/YYYY HH:mm')}</div>
              </div>
            ))}
            {/* 手机端 */}
            {showData.map((item, index) => (
              <div key={index} className={`flex items-center px-[10px] py-[12px] smjs:hidden border-b border-b-[#DCDCDC] `}>
                <Image src={'https://cos.spyx.com/' + item.iconUrl} width={36} height={36} alt='app' className="mr-3" onError={handleImageError} />
                <div className='w-[-webkit-fill-available]'>
                  <div className='flex justify-between items-center'>
                    <div className='text-[16px] leading-[24px] text-[#222222] font-[600]'>{item.app}</div>
                    <div className='text-[14px] leading-[24px] text-[#8A8A8E]'>{dayjs(item.time).format('DD/MM/YYYY HH:mm')}</div>
                  </div>
                  <div className='mt-[10px] text-[15px] leading-[22px] text-[#8A8A8E]'>{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {isLoading ? (
        <div className='absolute inset-0 z-[20] bg-[#FFF] rounded-[10px]'>
          <ContentLoading />
        </div>
      ) : null}
{(allData.length > 0 || isLoading) ||
          <div className='text-center px-5 absolute inset-0 flex justify-center items-center pointer-events-none'>
            {t('It appears theres no data to show here yet')}<br />
            {t('If data exists it may take up to 1 hour to download and appear')}<br />
            {t('We suggest checking back in a little while')}
          </div>}
      </ScrollDiv>
    </Fragment>
  )
}

export default Keylogger
